import { useNavigate } from '@fukuri-admins/hooks/common';
import { getStressAssessmentRole, getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StressAssessmentRole } from '~/types/model/stressAssessment';
import { useGetStressAssessmentSummary } from '~/usecases/useGetStressAssessmentSummary';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { Filter } from '../types';
import { useGetStressAssessmentUserReportList } from './useGetStressAssessmentUserReportList';

type Args = {
  stressAssessmentId: number;
};

type IUseStressAssessmentUserReportList = (args: Args) => {
  state: {
    role: StressAssessmentRole | undefined;
    userReportList: ReturnType<typeof useGetStressAssessmentUserReportList>['data'];
    summary: ReturnType<typeof useGetStressAssessmentSummary>['data'];
    isLoading: boolean;
    filter: Filter;
    isOpenRemindModal: boolean;
    threeLetterCode: string;
    currentPage: number;
    totalPage: number;
    totalCount: number;
  };
  actions: {
    handleFilterChange: (filter: Filter) => void;
    handlePageChange: (page: number) => void;
    handleOpenRemindModal: () => void;
    handleCloseRemindModal: () => void;
    handleRowClick: (userId: string) => void;
  };
};

export const useStressAssessmentUserReportList: IUseStressAssessmentUserReportList = ({
  stressAssessmentId,
}) => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const [filterParams, setFilterParams] = useSearchParams();
  const [isOpenRemindModal, setIsOpenRemindModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<Filter>('completed');

  const rawPage = Number.parseInt(filterParams.get('page') ?? '1', 10);
  const currentPage = Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;
  const role = getStressAssessmentRole();

  const handleFilterChange = (newFilter: Filter) => {
    setFilter(newFilter);
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', '1');
    setFilterParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', page.toString());
    setFilterParams(newParams);
  };

  const handleOpenRemindModal = () => setIsOpenRemindModal(true);
  const handleCloseRemindModal = () => setIsOpenRemindModal(false);

  const handleRowClick = (userId: string) => {
    const userIdNum = Number.parseInt(userId, 10);
    if (Number.isNaN(userIdNum)) return;

    navigate(
      replaceRouteParams(ROUTES.STRESS_ASSESSMENTS.USER_RESULT, {
        id: stressAssessmentId,
        user_id: userIdNum,
      })
    );
  };

  const { data: userReportList, headers } = useGetStressAssessmentUserReportList(threeLetterCode, {
    stressAssessmentId,
    filter,
    page: String(currentPage),
  });
  const { data: summary } = useGetStressAssessmentSummary(
    threeLetterCode,
    String(stressAssessmentId)
  );

  const totalPage = Number(headers?.['total-pages']) || 0;
  const totalCount = Number(headers?.['total-count']) || 0;

  return {
    state: {
      role,
      userReportList,
      summary,
      isLoading: !userReportList,
      filter,
      isOpenRemindModal,
      threeLetterCode,
      currentPage,
      totalPage,
      totalCount,
    },
    actions: {
      handleFilterChange,
      handlePageChange,
      handleOpenRemindModal,
      handleCloseRemindModal,
      handleRowClick,
    },
  };
};
